/**
 * Chat Service
 * Service for interacting with chat-related endpoints
 */

import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import axiosInstance from "utils/axios";

/**
 * Type definitions for chat messages
 */
export interface ChatMessage {
  _id?: string;
  content: string;
  role: "user" | "assistant" | "system";
  createdAt: Date;
}

/**
 * Type definitions for chat data
 */
export interface Chat {
  _id: string;
  title: string;
  messages: ChatMessage[];
  userId: string;
  tenantId: string;
  totalTokensUsed: number;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Type definition for contact information
 */
export interface ContactInfo {
  website?: string;
  email?: string;
  phone?: string;
}

/**
 * Type definition for token limits
 */
export interface TokenLimit {
  UID: string;
  limit: number;
  used: number;
}

/**
 * Type definition for tenant settings
 */
export interface TenantSettings {
  contactInfo?: ContactInfo;
  defaultQuestions?: string[];
  commonQuestions?: string[];
  maxTokens?: number;
  systemPrompt?: string;
  botName?: string;
  assistantId?: string; // OpenAI Assistant ID for this tenant
}

/**
 * Fetch contact information for a tenant
 * @param tenantId - The ID of the tenant
 * @returns The contact information for the tenant
 */
export const getContactInfo = async (
  tenantId: string
): Promise<ContactInfo> => {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_BASE_URL}/settings/contact/${tenantId}`
    );
    return response.data.data;
  } catch (error) {
    console.error("Error fetching contact info:", error);
    return {};
  }
};

/**
 * Fetch default questions for a tenant
 * @param tenantId - The ID of the tenant
 * @returns The default and common questions for the tenant
 */
export const getDefaultQuestions = async (tenantId: string) => {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_BASE_URL}/settings/questions/${tenantId}`
    );
    return response.data.data;
  } catch (error) {
    console.error("Error fetching default questions:", error);
    return { defaultQuestions: [], commonQuestions: [] };
  }
};

/**
 * Fetch all chats for a user
 * @param tenantId - The ID of the tenant
 * @param userId - The ID of the user
 * @returns An array of chats
 */
export const getUserChats = async (
  tenantId: string,
  userId: string
): Promise<Chat[]> => {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_BASE_URL}/chats/${tenantId}/${userId}`
    );
    return response.data.data;
  } catch (error) {
    console.error("Error fetching user chats:", error);
    return [];
  }
};

// Add this interface to define the OpenAI message format
interface OpenAIMessage {
  _id: string;
  content: string;
  role: "user" | "assistant" | "system";
  created_at: string | Date;
}

/**
 * Get a specific chat by ID and retrieve message history from OpenAI
 * @param tenantId - The ID of the tenant
 * @param userId - The ID of the user
 * @param chatId - The ID of the chat
 * @returns The chat with the specified ID along with messages from OpenAI
 */
export const getChatById = async (
): Promise<{ messages: ChatMessage[], tokens_used: any }> => {
  try {
    const response = await axiosInstance.get(
      'chatbot-chats'
    );

    // Extract chat and messages from response
    const { messages, tokens_used } = response.data.data;

    // Convert OpenAI message format to our ChatMessage format
    const formattedMessages: ChatMessage[] = Array.isArray(messages)
      ? messages.map((msg: OpenAIMessage) => ({
        _id: msg._id || undefined,
        content: msg.content,
        role: msg.role as "user" | "assistant" | "system",
        createdAt: new Date(msg.created_at)
      }))
      : [];

    return {
      messages: formattedMessages,
      tokens_used
    };
  } catch (error) {
    console.error("Error fetching chat:", error);
    return {  messages: [], tokens_used: {}};
  }
};

/**
 * Add a message to a chat
 * @param message - The content of the message
 * @param tokens - The number of tokens used by the message
 * @returns The updated chat
 */
export const addMessage = async (
  message: string,
  tokens?: number
): Promise<{ message: ChatMessage; chat: Chat } | null> => {
  try {
    const response = await axiosInstance.post(
      `chatbot-chats`,
      {
        message,
        tokens
      }
    );
    return response.data.data;
  } catch (error) {
    console.error("Error adding message:", error);
    return null;
  }
};

/**
 * Fetch all settings for a tenant
 * @param tenantId - The ID of the tenant
 * @returns All settings for the tenant
 */
export const getTenantSettings = async (
  tenantId: string
): Promise<TenantSettings> => {
  try {
    // First get the contact info
    const contactInfo = await getContactInfo(tenantId);

    // Then get the default questions
    const { defaultQuestions, commonQuestions } = await getDefaultQuestions(
      tenantId
    );

    // Return combined settings
    return {
      contactInfo,
      defaultQuestions,
      commonQuestions
    };
  } catch (error) {
    console.error("Error fetching tenant settings:", error);
    return {};
  }
};

/**
 * Check if a user's chat collection exists and is active
 * @param tenantId - The ID of the tenant
 * @param userId - The ID of the user
 * @returns Object with collection status and token information
 */
export const getChatSettings = async (): Promise<any> => {
  try {
    const response = await axiosInstance.get("chatbot-settiings");
    return response.data.data;
  } catch (error) {
    console.error("Error checking user chat collection:", error);
    return {
      exists: false,
      isActive: false,
      tokenInfo: null,
      settings: {}
    };
  }
};

export const useChatBotSettings = () => {
  return useQuery({
    queryKey: ["ChatBotSettings"],
    queryFn: getChatSettings
  })
}
